import { redactText } from '@hoedokloop/backup-crypto';

export const OBSERVABILITY_SCHEMA_VERSION = 1 as const;
export type ReleaseChannel = 'web' | 'ait';
export type LogSeverity = 'info' | 'warn' | 'error';
export type OperationalEvent =
  'request_completed' | 'request_failed' | 'dependency_unavailable' | 'client_error';

export type ReleaseContext = {
  channel: ReleaseChannel;
  buildVersion: string;
  contractVersion: number;
};

export type OperationalRecord = {
  schemaVersion: typeof OBSERVABILITY_SCHEMA_VERSION;
  event: OperationalEvent;
  severity: LogSeverity;
  occurredAt: string;
  requestId: string;
  release: ReleaseContext;
  route?: string;
  method?: string;
  status?: number;
  durationMs?: number;
  errorCode?: string;
  referenceId?: string;
};

const uuidSegment = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;
const opaqueSegment = /^[A-Za-z0-9_-]{20,}$/u;
const forbiddenKey =
  /(?:authsecret|authorization|credential|encryptionkey|examid|memo|notetext|recover|roomnickname|subject|title)/iu;
const allowedTagKeys = new Set(['boundary', 'feature', 'reason', 'route']);

export function normalizeRoutePath(pathname: string): string {
  const segments = pathname
    .split('/')
    .filter(Boolean)
    .slice(0, 6)
    .map((segment) => (uuidSegment.test(segment) || opaqueSegment.test(segment) ? ':id' : segment));
  return `/${segments.join('/')}`.slice(0, 160);
}

export function sanitizeDiagnosticTags(
  tags: Readonly<Record<string, string>> | undefined,
): Readonly<Record<string, string>> {
  if (!tags) return {};
  return Object.fromEntries(
    Object.entries(tags)
      .filter(([key]) => allowedTagKeys.has(key) && !forbiddenKey.test(key))
      .map(([key, value]) => [
        key,
        redactText(value)
          .replace(/[\r\n\t]/gu, ' ')
          .slice(0, 80),
      ]),
  );
}

export function classifyError(error: unknown): string {
  if (error instanceof DOMException) return `DOM_${error.name.toUpperCase().slice(0, 40)}`;
  if (error instanceof TypeError) return 'TYPE_ERROR';
  if (error instanceof RangeError) return 'RANGE_ERROR';
  if (error instanceof Error) return 'ERROR';
  return 'UNKNOWN_ERROR';
}

export function createErrorReference(createId: () => string = () => crypto.randomUUID()): string {
  return `PN-${createId().replaceAll('-', '').slice(0, 12).toUpperCase()}`;
}

export function createOperationalRecord(options: {
  event: OperationalEvent;
  severity: LogSeverity;
  occurredAt: string;
  requestId: string;
  release: ReleaseContext;
  route?: string;
  method?: string;
  status?: number;
  durationMs?: number;
  errorCode?: string;
  referenceId?: string;
}): OperationalRecord {
  const bounded = (value: string | undefined, max: number) =>
    value === undefined ? undefined : redactText(value).slice(0, max);
  return {
    schemaVersion: OBSERVABILITY_SCHEMA_VERSION,
    event: options.event,
    severity: options.severity,
    occurredAt: options.occurredAt,
    requestId: options.requestId,
    release: {
      channel: options.release.channel,
      buildVersion: bounded(options.release.buildVersion, 100) ?? 'unknown',
      contractVersion: options.release.contractVersion,
    },
    ...(options.route === undefined ? {} : { route: normalizeRoutePath(options.route) }),
    ...(options.method === undefined ? {} : { method: options.method.slice(0, 12).toUpperCase() }),
    ...(options.status === undefined ? {} : { status: options.status }),
    ...(options.durationMs === undefined
      ? {}
      : { durationMs: Math.max(0, Math.round(options.durationMs)) }),
    ...(options.errorCode === undefined
      ? {}
      : { errorCode: redactText(options.errorCode).slice(0, 64) }),
    ...(options.referenceId === undefined
      ? {}
      : { referenceId: redactText(options.referenceId).slice(0, 64) }),
  };
}
